import React, { useState } from 'react';
import { Lightbulb, HelpCircle, BookOpen, Gauge, ChevronDown, ChevronRight, Link2 } from 'lucide-react';
import { ActivatedCognitiveElement, LinkedNodeRef } from '../../services/workshop';

// Renders an avatar's cognitive-policy reasoning trace (facts considered,
// assumptions, evidence used, questions raised, confidence, which graph
// elements activated). Renders nothing if no trace fields are present, so it's
// safe to drop into any card unconditionally — non-cognitive avatars and
// legacy contributions look exactly as they did before this existed.
export interface CognitiveTrace {
  factsConsidered?: string[];
  assumptions?: string[];
  evidenceUsed?: string[];
  questionsRaised?: string[];
  confidence?: number;
  activatedElements?: ActivatedCognitiveElement[];
  reasoningDepth?: 'single' | 'multi-step';
  linkedNodes?: LinkedNodeRef[];
}

const ELEMENT_TYPE_STYLE: Record<ActivatedCognitiveElement['type'], { bg: string; text: string; border: string; label: string }> = {
  lens: { bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-100', label: 'Lens' },
  question: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-100', label: 'Question' },
  evidenceRule: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-100', label: 'Evidence rule' },
  decisionCriterion: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-100', label: 'Decision criterion' },
};

const TraceList: React.FC<{ icon: React.ReactNode; label: string; items?: string[] }> = ({ icon, label, items }) => {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1">
        {icon}
        {label}
      </div>
      <ul className="space-y-1">
        {items.map((item, i) => (
          <li key={i} className="text-xs text-slate-600 leading-snug pl-1 border-l-2 border-slate-200">
            <span className="pl-2">{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const CognitiveTraceDetail: React.FC<CognitiveTrace> = ({
  factsConsidered,
  assumptions,
  evidenceUsed,
  questionsRaised,
  confidence,
  activatedElements,
  reasoningDepth,
  linkedNodes,
}) => {
  const [linkedNodesExpanded, setLinkedNodesExpanded] = useState(false);

  const hasAnyTrace = !!(
    factsConsidered?.length ||
    assumptions?.length ||
    evidenceUsed?.length ||
    questionsRaised?.length ||
    activatedElements?.length ||
    linkedNodes?.length ||
    reasoningDepth
  );
  if (!hasAnyTrace) return null;

  return (
    <div className="mt-3 pt-3 border-t border-slate-100 space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        {reasoningDepth && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[11px] font-bold border border-slate-200">
            <Gauge size={11} />
            {reasoningDepth === 'multi-step' ? 'Deep reasoning' : 'Single-pass reasoning'}
          </span>
        )}
        {typeof confidence === 'number' && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[11px] font-bold border border-slate-200">
            Confidence {Math.round(confidence <= 1 ? confidence * 100 : confidence)}%
          </span>
        )}
      </div>

      <TraceList icon={<BookOpen size={12} />} label="Facts considered" items={factsConsidered} />
      <TraceList icon={<Lightbulb size={12} />} label="Assumptions" items={assumptions} />
      <TraceList icon={<BookOpen size={12} />} label="Evidence used" items={evidenceUsed} />
      <TraceList icon={<HelpCircle size={12} />} label="Questions raised" items={questionsRaised} />

      {activatedElements && activatedElements.length > 0 && (
        <div>
          <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">
            What shaped this
          </div>
          <div className="flex flex-wrap gap-1.5">
            {activatedElements.map((el) => {
              const style = ELEMENT_TYPE_STYLE[el.type];
              return (
                <span
                  key={el.id}
                  title={el.text}
                  className={`inline-flex items-center px-2 py-0.5 rounded-full ${style.bg} ${style.text} border ${style.border} text-[11px] font-medium max-w-[220px] truncate`}
                >
                  {el.text}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {linkedNodes && linkedNodes.length > 0 && (
        <div>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLinkedNodesExpanded(v => !v); }}
            className="flex items-center gap-1.5 text-[11px] font-bold text-slate-500 uppercase tracking-wide hover:text-slate-700"
          >
            {linkedNodesExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            <Link2 size={12} />
            Linked contributions &amp; evidence ({linkedNodes.length})
          </button>
          {linkedNodesExpanded && (
            <ul className="space-y-1 mt-1.5">
              {linkedNodes.map((node, i) => (
                <li
                  key={`${node.id}-${i}`}
                  className="flex items-baseline gap-2 text-xs text-slate-600 leading-snug pl-2 border-l-2 border-slate-200"
                >
                  <span className="shrink-0 font-bold text-slate-400 uppercase text-[10px] tracking-wide">
                    {node.type}
                  </span>
                  <span className="break-words">{node.label}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default CognitiveTraceDetail;
